import { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  ListChecks,
  Warning,
  Scales,
  LockKey,
  CircleNotch,
  CheckCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RowActions } from '@/components/shared/RowActions';
import { TaskModal } from '@/components/tasks/TaskModal';
import { TaskDetailPanel } from '@/components/tasks/TaskDetailPanel';
import { CompleteTaskDialog } from '@/components/tasks/CompleteTaskDialog';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import {
  fetchTasks,
  updateTask,
  deleteTask,
  STATUS_LABELS,
  PRIORITY_LABELS,
  ACTIVE_STATUSES,
  STATUS_WEIGHT,
  type Task,
  type TaskStatus,
} from '@/lib/tasksApi';
import { uploadDocument as uploadDocumentApi } from '@/lib/documentsApi';
import { getApiErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/features/auth/store';
import { useClerkWorkspaceStore } from '@/lib/clerkWorkspaceStore';
import { useChambersMode } from '@/lib/useChambersMode';

type Tab = 'all' | 'today' | 'overdue' | 'pending' | 'done';

const DELEGATION_STATUSES: TaskStatus[] = ['awaiting_barrister', 'awaiting_clerk'];

const PAGE_SIZE_OPTIONS = [10, 20, 30, 40];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function bucket(t: Task): 'overdue' | 'today' | 'week' | 'later' {
  if (!t.dueDate) return 'later';
  const d = new Date(t.dueDate);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(d); target.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  if (diff <= 7) return 'week';
  return 'later';
}

function fmtDue(iso: string | null): { label: string; overdue: boolean } {
  if (!iso) return { label: 'No due date', overdue: false };
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(d); target.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (diff === 0) return { label: 'Due today', overdue: false };
  if (diff === 1) return { label: 'Due tomorrow', overdue: false };
  if (diff === -1) return { label: 'Due yesterday', overdue: true };
  if (diff < 0) return { label: `${Math.abs(diff)}d overdue`, overdue: true };
  if (diff <= 7) return { label: `Due ${d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}`, overdue: false };
  return { label: `Due ${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`, overdue: false };
}

function statusBorderClass(status: TaskStatus): string {
  switch (status) {
    case 'blocked':                              return 'border-l-[3px] border-l-red-400';
    case 'in_progress':                          return 'border-l-[3px] border-l-brand-500';
    case 'awaiting_barrister':
    case 'awaiting_clerk':                       return 'border-l-[3px] border-l-amber-400';
    case 'hearing_followup':
    case 'filing_pending':                       return 'border-l-[3px] border-l-purple-400';
    default:                                     return 'border-l-[3px] border-l-transparent';
  }
}

function priorityBadge(p: string) {
  const map: Record<string, string> = {
    urgent: 'text-red-500 dark:text-red-400',
    high:   'text-amber-500 dark:text-amber-400',
    normal: 'hidden',
    low:    'text-blue-500 dark:text-blue-400',
  };
  return map[p] ?? '';
}

function statusBadge(s: TaskStatus) {
  if (s === 'pending') return 'hidden';
  const map: Record<TaskStatus, string> = {
    completed:          'text-emerald-600 dark:text-emerald-400',
    in_progress:        'text-brand-600 dark:text-brand-400',
    blocked:            'text-red-500 dark:text-red-400',
    cancelled:          'text-zinc-400',
    awaiting_barrister: 'text-amber-500 dark:text-amber-400',
    awaiting_clerk:     'text-amber-500 dark:text-amber-400',
    hearing_followup:   'text-purple-500 dark:text-purple-400',
    filing_pending:     'text-purple-500 dark:text-purple-400',
    pending:            'hidden',
  };
  return map[s];
}

export default function MyTasksPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCaseId = searchParams.get('caseId') ?? undefined;
  const currentUser = useAuthStore((s) => s.user);
  const { isSolo } = useChambersMode();

  const [tab, setTab] = useState<Tab>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Task | null>(null);
  const [detailTask, setDetailTask] = useState<Task | null>(null);
  const [completeTarget, setCompleteTarget] = useState<Task | null>(null);
  const [filterMonth, setFilterMonth] = useState<string>(''); // '' = all, else '0'..'11'
  const [filterYear, setFilterYear] = useState<string>('');   // '' = all, else year
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const openQ = useQuery({
    queryKey: ['tasks', 'open', { assignedToId: currentUser?.id }],
    queryFn: () => fetchTasks({ assignedToId: currentUser?.id, limit: 200, sortBy: 'dueDate', sortOrder: 'asc' }),
    enabled: Boolean(currentUser),
  });

  const doneQ = useQuery({
    queryKey: ['tasks', 'done-all', { assignedToId: currentUser?.id }],
    queryFn: () => fetchTasks({ assignedToId: currentUser?.id, status: 'completed', limit: 200, sortBy: 'completedAt', sortOrder: 'desc' }),
    enabled: Boolean(currentUser) && tab === 'done',
  });

  // "Nothing due today." must mean the list is clear, not that it never arrived. A 401/403/404 is
  // a deliberate refusal rather than a connection problem, so it does not count — same rule as the
  // barrister dashboard (pages/barrister/DashboardPage.tsx).
  const isLoadFailure = (q: { isError: boolean; error: unknown }) => {
    const status = (q.error as { response?: { status?: number } } | null)?.response?.status;
    return q.isError && status !== 401 && status !== 403 && status !== 404;
  };
  const activeLoadFailed = isLoadFailure(openQ);
  const doneLoadFailed = isLoadFailure(doneQ);

  const openTasks = useMemo(() => {
    const all = (openQ.data?.tasks ?? []).filter((t) => ACTIVE_STATUSES.includes(t.status));
    const filtered = isSolo ? all.filter((t) => !DELEGATION_STATUSES.includes(t.status)) : all;
    return [...filtered].sort((a, b) => {
      const wb = bucket(a) === 'overdue' ? -100 : STATUS_WEIGHT[a.status];
      const wa = bucket(b) === 'overdue' ? -100 : STATUS_WEIGHT[b.status];
      if (wb !== wa) return wb - wa;
      if (a.dueDate && b.dueDate) return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      return 0;
    });
  }, [openQ.data, isSolo]);

  const doneToday = useMemo(
    () => (doneQ.data?.tasks ?? []),
    [doneQ.data],
  );

  const matchesMonthYear = useCallback((iso: string | null) => {
    if (!filterMonth && !filterYear) return true;
    if (!iso) return false;
    const d = new Date(iso);
    if (filterYear && d.getFullYear() !== Number(filterYear)) return false;
    if (filterMonth && d.getMonth() !== Number(filterMonth)) return false;
    return true;
  }, [filterMonth, filterYear]);

  // Years present across the user's tasks — drives the Year dropdown.
  const availableYears = useMemo(() => {
    const ys = new Set<number>();
    for (const t of [...(openQ.data?.tasks ?? []), ...(doneQ.data?.tasks ?? [])]) {
      if (t.dueDate) ys.add(new Date(t.dueDate).getFullYear());
      if (t.completedAt) ys.add(new Date(t.completedAt).getFullYear());
    }
    return [...ys].sort((a, b) => b - a);
  }, [openQ.data, doneQ.data]);

  const counts = useMemo(() => {
    const c = { today: 0, overdue: 0, pending: 0, all: openTasks.length, done: doneToday.length };
    for (const t of openTasks) {
      const b = bucket(t);
      if (b === 'today') c.today++;
      else if (b === 'overdue') c.overdue++;
      if (t.status === 'pending') c.pending++;
    }
    return c;
  }, [openTasks, doneToday]);

  const visibleOpen = useMemo(() => {
    let list = openTasks;
    if (tab === 'today') list = openTasks.filter((t) => bucket(t) === 'today');
    else if (tab === 'overdue') list = openTasks.filter((t) => bucket(t) === 'overdue');
    else if (tab === 'pending') list = openTasks.filter((t) => t.status === 'pending');
    return list.filter((t) => matchesMonthYear(t.dueDate));
  }, [openTasks, tab, matchesMonthYear]);

  const visibleDone = useMemo(
    () => doneToday.filter((t) => matchesMonthYear(t.completedAt ?? t.updatedAt)),
    [doneToday, matchesMonthYear],
  );

  // Pagination over whichever list the active tab shows.
  const activeList = tab === 'done' ? visibleDone : visibleOpen;
  const totalPages = Math.max(1, Math.ceil(activeList.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedList = activeList.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Reset to the first page whenever the tab or filters change.
  useEffect(() => { setPage(1); }, [tab, filterMonth, filterYear, pageSize]);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['tasks'] });
    qc.invalidateQueries({ queryKey: ['matter-tasks'] });
    qc.invalidateQueries({ queryKey: ['diary-tasks'] });
  };

  const completeMut = useMutation({
    mutationFn: async ({ id, note, file, caseId }: { id: string; note: string; file?: File; caseId?: string }) => {
      await updateTask(id, { status: 'completed', completionNote: note });
      if (file && caseId) {
        await uploadDocumentApi(file, { caseId, category: 'task_completion' });
      }
    },
    onSuccess: () => {
      toast.success('Task completion recorded');
      setCompleteTarget(null);
      setDetailTask(null);
      invalidateAll();
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not record completion')),
  });

  const reopenMut = useMutation({
    mutationFn: (id: string) => updateTask(id, { status: 'pending' }),
    onSuccess: () => { toast.success('Task reopened'); setDetailTask(null); invalidateAll(); },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not reopen task')),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteTask(id),
    onSuccess: () => { toast.success('Task deleted'); setDetailTask(null); invalidateAll(); },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not delete task')),
  });

  const subtitleParts: string[] = [];
  if (counts.overdue > 0) subtitleParts.push(`${counts.overdue} overdue`);
  else if (counts.today > 0) subtitleParts.push(`${counts.today} due today`);
  if (counts.done > 0) subtitleParts.push(`${counts.done} completed today`);
  if (!subtitleParts.length && counts.all > 0) subtitleParts.push(`${counts.all} open`);
  // With no tasks loaded the subtitle would otherwise read "No open tasks" over a failed fetch.
  if (!subtitleParts.length) subtitleParts.push(activeLoadFailed ? 'Task list unavailable' : 'No open tasks');

  // One wording on every screen — a tab must not be called 'Completed' on a desktop and
  // 'Done' on a phone. Where five full labels overrun a narrow screen the strip scrolls.
  const tabs = [
    { key: 'all' as const,     label: 'All open',   count: counts.all },
    { key: 'today' as const,   label: 'Today',      count: counts.today },
    { key: 'overdue' as const, label: 'Overdue',    count: counts.overdue },
    { key: 'pending' as const, label: 'Pending',    count: counts.pending },
    { key: 'done' as const,    label: 'Completed',  count: counts.done },
  ];

  const hasManageTasks = useClerkWorkspaceStore((s) => s.hasPermission('MANAGE_TASKS'));

  const canEdit = (t: Task) =>
    t.assignedToId === currentUser?.id ||
    t.createdById === currentUser?.id ||
    currentUser?.memberType === 'admin' ||
    currentUser?.memberType === 'staff' ||
    hasManageTasks;

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="My Tasks"
        subtitle={subtitleParts.join(' · ')}
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>
            New task
          </Button>
        }
      />

      <div className="mb-4 flex items-center border-b border-border sm:gap-x-1">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px flex flex-1 items-center justify-center gap-1 whitespace-nowrap border-b-2 px-0.5 py-2.5 text-[11px] font-medium transition sm:gap-1.5 sm:px-4 sm:text-sm lg:flex-none lg:justify-start ${
              tab === t.key
                ? 'border-brand-600 text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
            {t.count > 0 && (
              <span className={`rounded-full px-1 py-0.5 text-[10px] font-semibold tabular-nums sm:px-1.5 ${
                t.key === 'overdue' && t.count > 0
                  ? 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters: month / year + rows per page */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">Filter:</span>
        <select
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          className="h-8 rounded-md border border-border bg-card px-2 text-xs focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
        >
          <option value="">All months</option>
          {MONTHS.map((m, i) => <option key={m} value={String(i)}>{m}</option>)}
        </select>
        <select
          value={filterYear}
          onChange={(e) => setFilterYear(e.target.value)}
          className="h-8 rounded-md border border-border bg-card px-2 text-xs focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
        >
          <option value="">All years</option>
          {availableYears.map((y) => <option key={y} value={String(y)}>{y}</option>)}
        </select>
        {(filterMonth || filterYear) && (
          <button
            onClick={() => { setFilterMonth(''); setFilterYear(''); }}
            className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline"
          >
            Clear
          </button>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Rows per page</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="h-8 rounded-md border border-border bg-card px-2 text-xs focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
          >
            {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      <Card className="overflow-hidden">
        {openQ.isLoading && (
          <div className="flex items-center gap-2 px-5 py-6 text-sm text-muted-foreground">
            <CircleNotch size={14} className="animate-spin" /> Loading…
          </div>
        )}

        {/* The task list could not be fetched — never shown as an empty list */}
        {activeLoadFailed && tab !== 'done' && (
          <QueryErrorState what="your tasks" onRetry={() => void openQ.refetch()} />
        )}

        {/* Active tasks */}
        {!openQ.isLoading && !activeLoadFailed && tab !== 'done' && (
          <>
            {activeList.length === 0 ? (
              <div className="px-5 py-12 text-center">
                <ListChecks className="mx-auto mb-3 h-8 w-8 text-muted-foreground/40" weight="duotone" />
                <div className="text-sm text-muted-foreground">
                  {tab === 'today' && 'Nothing due today.'}
                  {tab === 'overdue' && 'No overdue tasks.'}
                  {tab === 'pending' && 'No pending tasks.'}
                  {tab === 'all' && 'No open tasks.'}
                </div>
                <Button className="mt-4" variant="outline" size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>
                  New task
                </Button>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {pagedList.map((t) => {
                  const due = fmtDue(t.dueDate);
                  return (
                    <li
                      key={t.id}
                      className={`group cursor-pointer transition hover:bg-muted/20 ${statusBorderClass(t.status)}`}
                      onClick={() => setDetailTask(t)}
                    >
                      <div className="flex items-start gap-3 px-5 py-3.5">
                        <div className="min-w-0 flex-1">
                          {/* Title row */}
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            {t.isPrivate && (
                              <LockKey size={11} weight="fill" className="text-muted-foreground/40 shrink-0" />
                            )}
                            <span className="text-sm font-medium text-foreground">{t.title}</span>
                            {t.priority !== 'normal' && (
                              <span className={`text-[10px] font-medium uppercase tracking-wider ${priorityBadge(t.priority)}`}>
                                {PRIORITY_LABELS[t.priority as keyof typeof PRIORITY_LABELS]}
                              </span>
                            )}
                            {t.status !== 'pending' && (
                              <span className={`text-[10px] font-medium uppercase tracking-wider ${statusBadge(t.status)}`}>
                                {STATUS_LABELS[t.status]}
                              </span>
                            )}
                          </div>

                          {/* Meta row */}
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0 text-xs text-muted-foreground/70">
                            {t.case && (
                              <span className="flex items-center gap-1 font-medium">
                                <Scales size={10} weight="duotone" />
                                {t.case.caseNumber}
                              </span>
                            )}
                            {t.hearing && (
                              <span>
                                {t.hearing.hearingType} ·{' '}
                                {new Date(t.hearing.hearingDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                              </span>
                            )}
                            {t.assignedTo && !isSolo && (
                              <span>{t.assignedTo.firstName} {t.assignedTo.lastName}</span>
                            )}
                            <span className={due.overdue ? 'font-medium text-red-600 dark:text-red-400 flex items-center gap-0.5' : ''}>
                              {due.overdue && <Warning size={10} weight="fill" />}
                              {due.label}
                            </span>
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            title="Complete"
                            aria-label="Complete"
                            disabled={!canEdit(t)}
                            onClick={(e) => { e.stopPropagation(); setCompleteTarget(t); }}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-emerald-50 dark:bg-emerald-500/10 hover:text-emerald-600 dark:hover:text-emerald-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 disabled:pointer-events-none"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                          <RowActions
                            onView={() => setDetailTask(t)}
                            onEdit={canEdit(t) ? () => setEditTarget(t) : undefined}
                            onDelete={canEdit(t) ? () => deleteMut.mutate(t.id) : undefined}
                            deleteConfirm={{
                              title: 'Delete task?',
                              message: `Delete “${t.title}”? This removes it from the matter.`,
                              confirmLabel: 'Delete',
                            }}
                          />
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}

        {/* Completed (Done Today) */}
        {tab === 'done' && (
          <>
            {doneQ.isLoading && (
              <div className="flex items-center gap-2 px-5 py-6 text-sm text-muted-foreground">
                <CircleNotch size={14} className="animate-spin" /> Loading…
              </div>
            )}
            {doneLoadFailed && (
              <QueryErrorState what="your completed tasks" onRetry={() => void doneQ.refetch()} />
            )}
            {!doneQ.isLoading && !doneLoadFailed && activeList.length === 0 && (
              <div className="px-5 py-12 text-center">
                <ListChecks className="mx-auto mb-3 h-8 w-8 text-muted-foreground/40" weight="duotone" />
                <div className="text-sm text-muted-foreground">No completed tasks{(filterMonth || filterYear) ? ' for this period' : ''}.</div>
              </div>
            )}
            {!doneQ.isLoading && !doneLoadFailed && activeList.length > 0 && (
              <ul className="divide-y divide-border">
                {pagedList.map((t) => {
                  const completedTs = t.completedAt ?? t.updatedAt;
                  return (
                    <li
                      key={t.id}
                      className="group cursor-pointer border-l-[3px] border-l-emerald-400 transition hover:bg-muted/10"
                      onClick={() => setDetailTask(t)}
                    >
                      <div className="flex items-start gap-3 px-5 py-3.5">
                        <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-emerald-500" />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            {t.isPrivate && <LockKey size={11} weight="fill" className="text-muted-foreground/30 shrink-0" />}
                            <span className="text-sm text-foreground/75">{t.title}</span>
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground/60">
                            <span>
                              Completed {new Date(completedTs).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {!isSolo && t.completedBy && (
                              <>
                                <span className="text-muted-foreground/30">·</span>
                                <span>{t.completedBy.firstName} {t.completedBy.lastName}</span>
                              </>
                            )}
                            {t.case && (
                              <>
                                <span className="text-muted-foreground/30">·</span>
                                <span className="flex items-center gap-1">
                                  <Scales size={10} weight="duotone" />
                                  {t.case.caseNumber}
                                </span>
                              </>
                            )}
                          </div>
                          {t.completionNote && (
                            <div className="mt-1.5 rounded border-l-2 border-emerald-200 dark:border-emerald-500/30 pl-2.5 text-xs text-muted-foreground/70 italic">
                              {t.completionNote}
                            </div>
                          )}
                        </div>
                        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                          <RowActions
                            onView={() => setDetailTask(t)}
                            onDelete={canEdit(t) ? () => deleteMut.mutate(t.id) : undefined}
                            deleteConfirm={{
                              title: 'Delete task?',
                              message: `Delete “${t.title}”? This removes it from the matter.`,
                              confirmLabel: 'Delete',
                            }}
                          />
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}

        {/* Pagination */}
        {activeList.length > pageSize && (
          <div className="flex items-center justify-between border-t border-border px-5 py-3 text-xs text-muted-foreground">
            <div>
              Showing <span className="text-foreground">{(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, activeList.length)}</span> of <span className="text-foreground">{activeList.length}</span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="xs" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}>Previous</Button>
              <span className="px-2 text-foreground">{safePage} / {totalPages}</span>
              <Button variant="outline" size="xs" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}>Next</Button>
            </div>
          </div>
        )}
      </Card>

      {/* Task detail panel */}
      {detailTask && (
        <TaskDetailPanel
          task={detailTask}
          isSolo={isSolo}
          canEdit={canEdit(detailTask)}
          onClose={() => setDetailTask(null)}
          onEdit={() => { setEditTarget(detailTask); setDetailTask(null); }}
          onComplete={() => setCompleteTarget(detailTask)}
          onReopen={() => reopenMut.mutate(detailTask.id)}
          onDelete={() => {
            if (window.confirm(`Delete "${detailTask.title}"?`)) {
              deleteMut.mutate(detailTask.id);
            }
          }}
          onNavigateToMatter={(matterId) => navigate(`/barrister/matters/${matterId}`)}
        />
      )}

      {/* Complete dialog */}
      {completeTarget && currentUser && (
        <CompleteTaskDialog
          task={completeTarget}
          completingUserName={`${currentUser.firstName} ${currentUser.lastName}`}
          isPending={completeMut.isPending}
          onClose={() => setCompleteTarget(null)}
          onConfirm={(note, file) =>
            completeMut.mutate({
              id: completeTarget.id,
              note,
              file,
              caseId: completeTarget.caseId ?? undefined,
            })
          }
        />
      )}

      {createOpen && currentUser && (
        <TaskModal
          defaultAssigneeId={currentUser.id}
          matterId={preselectedCaseId}
          onClose={() => setCreateOpen(false)}
          onSaved={() => setCreateOpen(false)}
        />
      )}
      {editTarget && (
        <TaskModal
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => setEditTarget(null)}
        />
      )}
    </div>
  );
}
